// Block K (UI-22): the Transactions section of Invoicing, on the v2 kit (transactions.css, prefix txn-).
//
// MONEY IN IS MONEY THAT ARRIVED (Block K, failing-first in TransactionsList.test.js). Every invoice
// used to be listed as income at its full total, drafts, cancelled and unpaid ones included, while
// the empty state promised "Paid invoices". Now:
//   - money in: an invoice the client paid (utils/invoiceOverdue isPaidStatus), dated when paid;
//   - money out: each refund on it (its own date and amount), and every expense not rejected;
//   - decision 72: totals are kept per currency and never added together ("£100.00 plus €100.00").
//     Expenses carry no currency, so they are in the base currency (currentUser.baseCurrency, else
//     GBP), as on Financials.
import { useState, useMemo } from "react";
import { useInvoices } from "../../contexts/InvoicesContext";
import { useExpenses } from "../../contexts/ExpensesContext";
import { useCurrentUser } from "../../contexts/CurrentUserContext";
import { Button, StatTile, SearchInput, ChipGroup, List, ListRow, RowPrimary, StatusPill, EmptyState, ErrorState, Skeleton } from "../../ui";
import { formatMoney } from "../../utils/currency";
import { isPaidStatus } from "../../utils/invoiceOverdue";
import SectionHead from "./SectionHead";
import "./invoicing.css";
import "./transactions.css";

// A date-only string is read at midday local time, so it never shows as the day before.
function parseDay(str) {
  if (!str) return null;
  const s = String(str);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T12:00:00`) : new Date(s);
  return isNaN(d.getTime()) ? null : d;
}
function fmtDate(str) {
  const d = parseDay(str);
  return d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
}

const TYPE_LABELS = { income: 'Money in', expense: 'Money out' };

const RANGE_OPTIONS = [
  { value: 'all',         label: 'All time' },
  { value: 'this-month',  label: 'This month' },
  { value: 'last-month',  label: 'Last month' },
  { value: 'this-year',   label: 'This year' },
];

const TYPE_OPTIONS = [
  { value: 'all',     label: 'All' },
  { value: 'income',  label: 'Money in' },
  { value: 'expense', label: 'Money out' },
];

function inRange(dateStr, key) {
  if (key === 'all' || !dateStr) return true;
  const d = parseDay(dateStr);
  if (!d) return true;
  const now = new Date();
  if (key === 'this-month') return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  if (key === 'last-month') {
    const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return d.getFullYear() === lm.getFullYear() && d.getMonth() === lm.getMonth();
  }
  if (key === 'this-year') return d.getFullYear() === now.getFullYear();
  return true;
}

const invoiceGross = (lineItems = []) => (lineItems || []).reduce(
  (s, r) => s + (Number(r.qty) || 0) * (Number(r.unitPrice) || 0) * (1 + (Number(r.tax) || 0) / 100), 0,
);

// Every money movement: paid invoices in, their refunds and the (not rejected) expenses out.
export function buildTransactions(invoices, expenses, baseCurrency) {
  const rows = [];
  for (const inv of invoices || []) {
    if (!isPaidStatus(inv.status)) continue;
    const currency = inv.currency || 'GBP';
    const label = `Invoice ${inv.number || inv.id}, ${inv.contactName || 'Client'}`;
    const paidOn = inv.paidAt || inv.issueDate;
    rows.push({
      id: `inv_${inv.id}`,
      date: paidOn,
      description: label,
      type: 'income',
      category: 'Invoice',
      amount: invoiceGross(inv.lineItems),
      currency,
      reference: inv.number || '',
    });
    const refunds = Array.isArray(inv.refunds) ? inv.refunds : [];
    if (refunds.length) {
      refunds.forEach((r, i) => rows.push({
        id: `ref_${inv.id}_${r.id ?? i}`,
        date: r.refundedOn || paidOn,
        description: `Refund on invoice ${inv.number || inv.id}, ${inv.contactName || 'Client'}`,
        type: 'expense',
        category: 'Refund',
        amount: Number(r.amount || 0),
        currency,
        reference: inv.number || '',
      }));
    } else if (Number(inv.refundedAmount || 0) > 0) {
      rows.push({
        id: `ref_${inv.id}`,
        date: paidOn,
        description: `Refund on invoice ${inv.number || inv.id}, ${inv.contactName || 'Client'}`,
        type: 'expense',
        category: 'Refund',
        amount: Number(inv.refundedAmount),
        currency,
        reference: inv.number || '',
      });
    }
  }
  for (const exp of expenses || []) {
    if (exp.approval_status === 'rejected') continue;
    rows.push({
      id: `exp_${exp.id}`,
      date: exp.date,
      description: exp.title || 'Expense',
      type: 'expense',
      category: exp.category || 'General',
      amount: Number(exp.amount || 0),
      currency: baseCurrency,
      reference: '',
    });
  }
  rows.sort((a, b) => (parseDay(b.date)?.getTime() || 0) - (parseDay(a.date)?.getTime() || 0));
  return rows;
}

// The base currency first, then the others A to Z; zeros dropped, never an empty answer.
function moneyText(map, base) {
  const codes = Object.keys(map).sort((a, b) => (a === base ? -1 : b === base ? 1 : a.localeCompare(b)));
  const parts = codes.filter(c => Math.abs(map[c]) >= 0.005).map(c => formatMoney(map[c], c));
  return parts.length ? parts.join(' plus ') : formatMoney(0, base);
}

const signed = (r) => `${r.type === 'income' ? '+' : '−'}${formatMoney(r.amount, r.currency)}`;

const COLUMNS = [
  { key: 'date', label: 'Date', width: 'minmax(96px, 0.8fr)' },
  { key: 'description', label: 'Description', role: 'primary', width: 'minmax(0, 2.4fr)' },
  { key: 'category', label: 'Category', width: 'minmax(0, 1fr)', truncate: true },
  { key: 'type', label: 'Type', role: 'status', width: 'minmax(0, 0.9fr)' },
  { key: 'amount', label: 'Amount', align: 'end', width: 'minmax(0, 1fr)' },
];

export default function TransactionsList() {
  const [range, setRange] = useState('this-month');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const { invoices, loaded = true, loadFailed = false, refresh } = useInvoices();
  const { expenses } = useExpenses();
  const { currentUser } = useCurrentUser();
  const baseCurrency = String(currentUser?.baseCurrency || 'GBP').toUpperCase();

  const raw = useMemo(() => buildTransactions(invoices, expenses, baseCurrency), [invoices, expenses, baseCurrency]);

  const rows = useMemo(() => raw.filter(r => {
    if (!inRange(r.date, range)) return false;
    if (typeFilter !== 'all' && r.type !== typeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return r.description.toLowerCase().includes(q) || r.category.toLowerCase().includes(q) || r.reference.toLowerCase().includes(q);
    }
    return true;
  }), [raw, range, typeFilter, search]);

  // Per currency (decision 72).
  const totals = useMemo(() => {
    const inn = {}, out = {}, net = {};
    rows.forEach(r => {
      const m = r.type === 'income' ? inn : out;
      m[r.currency] = (m[r.currency] || 0) + r.amount;
      net[r.currency] = (net[r.currency] || 0) + (r.type === 'income' ? r.amount : -r.amount);
    });
    return { inn, out, net };
  }, [rows]);

  function exportCSV() {
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = 'Date,Description,Type,Category,Currency,Amount,Reference\n';
    const body = rows.map(r =>
      [fmtDate(r.date), r.description, r.type, r.category, r.currency, (r.type === 'income' ? r.amount : -r.amount).toFixed(2), r.reference].map(esc).join(',')
    ).join('\n');
    const blob = new Blob([header + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${range}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const ready = loaded && !loadFailed;

  return (
    <div className="invc-section txn">
      <SectionHead
        title="Transactions"
        subline={ready ? `${rows.length} transaction${rows.length !== 1 ? 's' : ''}` : 'Money in and money out, in one list'}
        action={<Button variant="secondary" onClick={exportCSV} disabled={!ready}>Export CSV</Button>}
      />

      {loadFailed ? (
        <ErrorState
          title="Transactions did not load"
          message="Your invoices could not be loaded, so money in is not shown."
          onRetry={refresh}
        />
      ) : !loaded ? (
        <Skeleton lines={4} label="Loading transactions" />
      ) : (<>
        <section className="invc-kpis" aria-label="Transaction totals">
          <StatTile card color="pastel-green" label="Money in" value={moneyText(totals.inn, baseCurrency)} hint="Paid invoices" />
          <StatTile card color="pastel-pink" label="Money out" value={moneyText(totals.out, baseCurrency)} hint="Expenses and refunds" />
          <StatTile card color="pastel-lilac" label="Net" value={moneyText(totals.net, baseCurrency)} hint="Money in minus money out" />
        </section>

        <div className="invc-bar">
          <SearchInput value={search} onChange={e => setSearch(e.target.value)} placeholder="Search transactions…" label="Search transactions" />
          <div className="invc-bar__filters">
            <ChipGroup label="Type" options={TYPE_OPTIONS} value={typeFilter} onChange={setTypeFilter} />
            <ChipGroup label="Period" options={RANGE_OPTIONS} value={range} onChange={setRange} />
          </div>
        </div>

        {rows.length === 0 ? (
          <EmptyState icon="💷" title="No transactions found for this period">
            Paid invoices, refunds and logged expenses will appear here.
          </EmptyState>
        ) : (
          <List label="Transactions" columns={COLUMNS}>
            {rows.map(r => (
              <ListRow
                key={r.id}
                cells={{
                  date: <span className="txn-date">{fmtDate(r.date)}</span>,
                  description: <RowPrimary name={r.description} sub={r.reference ? `#${r.reference}` : undefined} />,
                  category: r.category,
                  type: <StatusPill tone={r.type === 'income' ? 'success' : 'neutral'}>{TYPE_LABELS[r.type]}</StatusPill>,
                  amount: <span className="invc-money txn-amount">{signed(r)}</span>,
                }}
                meta={`${fmtDate(r.date)} · ${r.category} · ${signed(r)}`}
              />
            ))}
          </List>
        )}
      </>)}
    </div>
  );
}
